import React, { useCallback, useEffect, useState } from "react";
import "./admin.css";

const API = process.env.REACT_APP_BACKEND_URL;
const KEY_STORAGE = "bp-admin-key";

const toCsv = (rows, columns) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [columns.map(esc).join(","), ...rows.map((r) => columns.map((c) => esc(r[c])).join(","))].join("\n");
};

const download = (filename, text) => {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "");

const Table = ({ id, title, rows, columns, labels }) => (
  <section className="ad-section" aria-labelledby={`${id}-title`} data-testid={`admin-${id}`}>
    <div className="ad-section-head">
      <h2 id={`${id}-title`} className="ad-h2">{title} <span className="ad-count" data-testid={`admin-${id}-count`}>{rows.length}</span></h2>
      <button type="button" className="bp-btn ad-btn" onClick={() => download(`${id}.csv`, toCsv(rows, columns))} disabled={!rows.length} data-testid={`admin-${id}-download`}>
        Download CSV
      </button>
    </div>
    <div className="ad-table-wrap">
      <table className="ad-table">
        <thead>
          <tr>{columns.map((c) => <th key={c} scope="col">{labels[c] || c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="ad-empty">No entries yet</td></tr>}
          {rows.map((r) => (
            <tr key={r.id}>{columns.map((c) => <td key={c}>{c === "created_at" ? fmt(r[c]) : c === "email_sent" ? (r[c] ? "Yes" : "No") : r[c]}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const SIGNUP_COLS = ["email", "created_at"];
const ENQUIRY_COLS = ["created_at", "name", "email", "mobile", "company", "team_size", "training_focus", "preferred_dates", "message", "email_sent"];
const BF_COLS = ENQUIRY_COLS.filter((c) => c !== "training_focus");
const LABELS = { email: "Email", created_at: "Date", name: "Name", mobile: "Mobile", company: "Company", team_size: "Team size", training_focus: "Focus", preferred_dates: "Preferred dates", message: "Message", email_sent: "Emailed" };

export default function AdminPage() {
  const [key, setKey] = useState(() => localStorage.getItem(KEY_STORAGE) || "");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { document.title = "Admin | The Business Playwright"; }, []);

  const load = useCallback(async (k) => {
    setError("");
    const headers = { "X-Admin-Key": k };
    const [a, b] = await Promise.all([
      fetch(`${API}/api/ticket-alerts`, { headers }),
      fetch(`${API}/api/enquiries`, { headers }),
    ]);
    if (a.status === 401 || b.status === 401) {
      localStorage.removeItem(KEY_STORAGE);
      setKey("");
      setData(null);
      setError("That key was not recognised.");
      return;
    }
    if (!a.ok || !b.ok) { setError("Could not load data. Please try again."); return; }
    setData({ signups: (await a.json()).signups, enquiries: (await b.json()).enquiries });
  }, []);

  useEffect(() => { if (key) load(key); }, [key, load]);

  const submitKey = (e) => {
    e.preventDefault();
    const k = input.trim();
    if (!k) return;
    localStorage.setItem(KEY_STORAGE, k);
    setKey(k);
  };

  const signOut = () => { localStorage.removeItem(KEY_STORAGE); setKey(""); setData(null); };

  return (
    <div className="bp-site ad-page" data-testid="admin-page">
      <main id="main" className="bp-wrap ad-main">
        <header className="ad-head">
          <h1 className="ad-h1">The Business Playwright — Admin</h1>
          {key && <button type="button" className="ad-link" onClick={signOut} data-testid="admin-sign-out">Sign out</button>}
        </header>

        {!key && (
          <form className="ad-key-form" onSubmit={submitKey} data-testid="admin-key-form">
            <label htmlFor="ad-key" className="ad-label">Admin key</label>
            <div className="ad-key-row">
              <input id="ad-key" type="password" className="ad-input" value={input} onChange={(e) => setInput(e.target.value)} autoComplete="off" data-testid="admin-key-input" />
              <button type="submit" className="bp-btn ad-btn" data-testid="admin-key-submit">Enter</button>
            </div>
            {error && <p className="ad-error" role="alert" data-testid="admin-error">{error}</p>}
          </form>
        )}

        {key && !data && !error && <p className="ad-loading" data-testid="admin-loading">Loading…</p>}
        {key && error && <p className="ad-error" role="alert" data-testid="admin-error">{error}</p>}

        {data && (
          <>
            <Table id="ticket-alerts" title="Ticket alert sign-ups" rows={data.signups} columns={SIGNUP_COLS} labels={LABELS} />
            <Table id="takeover-enquiries" title="The Takeover enquiries" rows={data.enquiries.filter((e) => e.production === "the-takeover")} columns={ENQUIRY_COLS} labels={LABELS} />
            <Table id="blind-faith-enquiries" title="Blind Faith enquiries" rows={data.enquiries.filter((e) => e.production === "blind-faith")} columns={BF_COLS} labels={LABELS} />
            <Table id="phone-enquiries" title="How to Turn Your Phone Off enquiries" rows={data.enquiries.filter((e) => e.production === "how-to-turn-your-phone-off")} columns={BF_COLS} labels={LABELS} />
          </>
        )}
      </main>
    </div>
  );
}
